const TAB_SELECTOR = '.st-filter-tab';

/**
 * Owns the Search Terms candidate filter state, its restore path, and keyboard
 * navigation. Candidate rendering stays in Step2 so the controller remains
 * independent of the chip DOM and selected-keyword writeback contract.
 */
export function createSearchTermsCandidateFilterController({
  select,
  selectAll,
  getState,
  setState,
  timers = globalThis,
} = {}) {
  let filterDebounce = null;
  const getTabs = () => Array.from(selectAll?.(TAB_SELECTOR) || [])
    .filter((tab) => !tab.disabled);

  function getCategoryMap() {
    const categories = getState?.('keywordLibrary')?.categories || {};
    const map = new Map();
    for (const [categoryKey, items] of Object.entries(categories)) {
      for (const item of items || []) {
        const keyword = String(item?.keyword || '').trim().toLowerCase();
        if (keyword && !map.has(keyword)) map.set(keyword, categoryKey);
      }
    }
    return map;
  }

  function getFilterState() {
    return {
      category: getState?.('stCandidateFilterCategory') || 'all',
      text: String(getState?.('stCandidateFilterText') || '').trim().toLowerCase(),
    };
  }

  function matches(keyword, item, selectedSet, categoryMap, filter = getFilterState()) {
    const text = String(keyword || '').trim().toLowerCase();
    if (!text || (filter.text && !text.includes(filter.text))) return false;
    if (filter.category === 'selected') return selectedSet.has(keyword);
    if (filter.category === 'unselected') return !selectedSet.has(keyword);
    if (filter.category === 'all') return true;
    return (categoryMap.get(text) || String(item?.category || '').trim()) === filter.category;
  }

  function activate(tab) {
    if (!tab || tab.disabled) return;
    setState?.('stCandidateFilterCategory', tab.dataset.category || 'all');
  }

  function sync() {
    const filter = getFilterState();
    for (const tab of getTabs()) {
      const isActive = (tab.dataset.category || 'all') === filter.category;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-pressed', String(isActive));
    }
    const input = select?.('#st-candidate-filter');
    const value = getState?.('stCandidateFilterText') || '';
    if (input && input.value !== value) input.value = value;
  }

  function handleTabClick(event) {
    const tab = event.target?.closest?.(TAB_SELECTOR);
    if (tab) activate(tab);
  }

  function handleTabKeydown(event) {
    const tab = event.target?.closest?.(TAB_SELECTOR);
    if (!tab) return;
    const tabs = getTabs();
    const index = tabs.indexOf(tab);
    if (index < 0) return;
    let targetIndex = index;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') targetIndex = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') targetIndex = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') targetIndex = 0;
    else if (event.key === 'End') targetIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    const target = tabs[targetIndex];
    target.focus();
    activate(target);
  }

  function handleFilterInput(event) {
    if (filterDebounce) timers.clearTimeout(filterDebounce);
    const value = event.target?.value || '';
    filterDebounce = timers.setTimeout(() => {
      filterDebounce = null;
      setState?.('stCandidateFilterText', value);
    }, 200);
  }

  function bind() {
    select?.('#st-candidate-tabs')?.addEventListener('click', handleTabClick);
    select?.('#st-candidate-tabs')?.addEventListener('keydown', handleTabKeydown);
    select?.('#st-candidate-filter')?.addEventListener('input', handleFilterInput);
  }

  return {
    activate,
    bind,
    getCategoryMap,
    getFilterState,
    matches,
    sync,
  };
}
